import { useEffect, useState } from "react";
import { useLatestRequest } from "../../../hooks/useLatestRequest";
import { getApiErrorMessage } from "../../../lib/result-codes";
import { getMerchantCategoryParentOptions } from "../api/merchantCategory.api";
import type { MerchantCategoryParentOption } from "../types/merchantCategory.types";

const parentOptionsPageSize = 100;

type UseMerchantCategoryParentOptionsArgs = {
  enabled: boolean;
  excludeId?: string;
};

export function useMerchantCategoryParentOptions({
  enabled,
  excludeId,
}: UseMerchantCategoryParentOptionsArgs) {
  const [options, setOptions] = useState<MerchantCategoryParentOption[]>([]);
  const [error, setError] = useState<string | null>(null);
  const { run, cancel, loading } = useLatestRequest();

  useEffect(() => {
    if (!enabled) {
      cancel();
      setOptions([]);
      setError(null);
      return;
    }

    const load = async () => {
      setError(null);
      try {
        const response = await run((signal) =>
          getMerchantCategoryParentOptions(
            {
              exclude_id: excludeId,
              page: 1,
              page_size: parentOptionsPageSize,
            },
            signal,
          ),
        );
        if (response) setOptions(response.list);
      } catch (requestError: unknown) {
        setOptions([]);
        setError(getApiErrorMessage(requestError, "Failed to load parent merchant categories"));
      }
    };

    void load();
  }, [cancel, enabled, excludeId, run]);

  return { options, error, isLoading: loading };
}
